// ProductOptions — the configurator dropdowns + dimension inputs.
//
// Renders Material, Adhesive, Shape, Corners, Dimensions/Size, Color,
// Quantity, finishing options (Lamination/Varnish/Foil), and roll parameters
// (Core / Labels-per-roll / Outside diameter / Unwind).  All state lives in
// the parent's `config` object; this component just calls `setConfig`.
import { useMemo, useState } from "react";
import { Minus, Plus, Layers, Pencil } from "lucide-react";
import { SelectRow, DimInput } from "./configuratorControls";
import { money } from "../lib/api";
import LinenPicker from "./LinenPicker";
import HangTagFinishing from "./HangTagFinishing";
import linenCatalog from "../data/linen-catalog.json";

/** True when a cover-material option name asks the customer to pick a linen. */
function isLinenOption(name) {
  if (!name) return false;
  const n = name.toLowerCase();
  return n.includes("linen") || n.includes("lenin");  // tolerant of typo
}

// Large-format products (banners, retractables) are priced per-unit and
// customers typically order 1 – 25 of them, not bulk tiers like labels.
const LARGE_FORMAT_CATEGORIES = new Set(["retractables-banners"]);

// Shared label helper.
//
// Historically we appended "(+$X)" / "(+Y%)" suffixes to every finishing
// row so customers could see the markup inline. As of iter17 the user
// asked to HIDE all backend markups from customer-facing labels — the
// "Your Price" card on the right side already reflects the live total,
// and the percent/flat amounts are internal accounting info that
// shouldn't leak into the storefront.
//
// Keep the helper signature so the existing call sites compile; just
// return an empty string. If we ever want to bring labels back per-row,
// flip a feature flag here.
function modeSuffix() {
  return "";
}

function LargeFormatQtyStepper({ value, onChange }) {
  const v = Math.max(1, parseInt(value) || 1);
  const change = (delta) => onChange(Math.max(1, v + delta));
  return (
    <div data-testid="qty-stepper-row">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
        Quantity
      </div>
      <div className="inline-flex items-stretch border border-slate-300">
        <button
          type="button"
          onClick={() => change(-1)}
          disabled={v <= 1}
          className="px-3 h-11 bg-white hover:bg-kolt-slate hover:text-white transition-colors disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-current border-r border-slate-300"
          data-testid="qty-minus-btn"
          aria-label="Decrease quantity"
        >
          <Minus className="w-4 h-4" />
        </button>
        <input
          type="number"
          min={1}
          value={v}
          onChange={(e) => onChange(Math.max(1, parseInt(e.target.value) || 1))}
          className="w-20 text-center font-mono font-bold text-lg bg-white outline-none"
          data-testid="qty-input"
        />
        <button
          type="button"
          onClick={() => change(1)}
          className="px-3 h-11 bg-white hover:bg-kolt-orange hover:text-white transition-colors border-l border-slate-300"
          data-testid="qty-plus-btn"
          aria-label="Increase quantity"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mt-1.5">
        Each unit priced individually · No bulk tiers
      </p>
    </div>
  );
}

export default function ProductOptions({ product, config, setConfig }) {
  const enabledOptionGroups = useMemo(
    () => (product.custom_option_groups || []).filter((g) => g.enabled !== false && (g.items || []).length > 0),
    [product.custom_option_groups],
  );
  const [linenPickerOpen, setLinenPickerOpen] = useState(false);
  const hasCustomDims = !!product.custom_dimensions;
  const isSqftMode = product.size_input_mode === "sqft" && Number(product.price_per_sqft) > 0;
  const isCircle = (config.shape || "").toLowerCase() === "circle";
  // Round shapes (circle, oval, ellipse) have no corners, so the
  // "Corners: Square / Rounded" dropdown only makes sense for rectangular
  // outlines. Hide it whenever the customer picks a round shape.
  const isRoundShape = ["circle", "oval", "ellipse"].includes(
    (config.shape || "").toLowerCase()
  );
  const updateConfig = (patch) => {
    let next = { ...config, ...patch };
    // Circle has only one dimension (diameter). Keep width === height.
    if (patch.shape && patch.shape.toLowerCase() === "circle") {
      next.width_in = next.height_in || next.width_in || product.custom_dimensions?.default_height || 1;
      next.height_in = next.width_in;
    } else if (patch.shape && patch.shape.toLowerCase() !== "circle" && (config.shape || "").toLowerCase() === "circle") {
      // Switching OUT of circle — keep height, leave width unchanged.
    }
    // When user edits the height of a circle, mirror it to width.
    if (patch.height_in !== undefined && (next.shape || "").toLowerCase() === "circle") {
      next.width_in = patch.height_in;
    }
    setConfig(next);
  };

  return (
    <>
      <div className="space-y-4">
        <SelectRow
          label="Material"
          value={config.material_key}
          onChange={(v) => updateConfig({ material_key: v })}
          options={product.materials.map((m) => ({ value: m.key, label: m.name, image: m.image_url || "" }))}
          testid="select-material"
        />
        {product.adhesives?.length > 0 && (
          <SelectRow
            label="Adhesive"
            value={config.adhesive_key}
            onChange={(v) => updateConfig({ adhesive_key: v })}
            options={product.adhesives.map((a) => ({ value: a.key, label: a.name }))}
            testid="select-adhesive"
          />
        )}
        <SelectRow
          label="Shape"
          value={config.shape}
          onChange={(v) => updateConfig({ shape: v })}
          options={product.shapes.map((s) => ({ value: s, label: s }))}
          testid="select-shape"
        />
        {product.corners?.length > 0 && !isRoundShape && (
          <SelectRow
            label="Corners"
            value={config.corners}
            onChange={(v) => updateConfig({ corners: v })}
            options={product.corners.map((c) => ({ value: c, label: c }))}
            testid="select-corners"
          />
        )}
        {isSqftMode ? (
          <SqftSizeInput
            product={product}
            config={config}
            updateConfig={updateConfig}
          />
        ) : hasCustomDims ? (
          <>
          <div className="grid grid-cols-2 gap-3" data-testid="custom-dimensions">
            <DimInput
              label={isCircle ? "Diameter (in)" : "Height (in)"}
              value={config.height_in}
              onChange={(v) => updateConfig({ height_in: v })}
              cd={product.custom_dimensions}
              axis="height"
              testid="input-height"
            />
            {isCircle ? (
              <div data-testid="input-width-locked">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                  Width (in)
                </div>
                <div className="input-base h-11 bg-slate-100 text-slate-500 flex items-center cursor-not-allowed">
                  Locked — circle uses one dimension
                </div>
              </div>
            ) : (
              <DimInput
                label="Width (in)"
                value={config.width_in}
                onChange={(v) => updateConfig({ width_in: v })}
                cd={product.custom_dimensions}
                axis="width"
                testid="input-width"
              />
            )}
          </div>
          {(Number(product.label_model?.custom_die_fee) > 0 || Number(product.die_cut_surcharge) > 0) && (
            <DieCutHelper
              die_cut_surcharge={product.label_model?.custom_die_fee || product.die_cut_surcharge}
              halfInchSteps={!!product.label_model}
              shape={config.shape}
              widthIn={config.width_in}
              heightIn={config.height_in}
              onUseStandard={(w, h) => updateConfig({ width_in: w, height_in: h })}
            />
          )}
          </>
        ) : (
          <SelectRow
            label="Size"
            value={config.size}
            onChange={(v) => updateConfig({ size: v })}
            // Sizes may be plain strings (legacy) OR objects with
            // {key, name, percent_uplift}. The percent_uplift is INTERNAL
            // (it tells the engine how much to mark up vs the baseline);
            // the customer should only see the size name — never the
            // markup percentage. The "Your Price" card on the right
            // updates live to reflect the actual cost.
            options={product.sizes.map((s) => {
              if (typeof s === "string") return { value: s, label: s };
              const label = s.name || s.key || "";
              return { value: s.key || s.name, label };
            })}
            testid="select-size"
          />
        )}
        {/* Color dropdown.
            • If the admin populated structured `color_options` (with key,
              mode, flat/percent), use them — customer config carries
              `color_key` and the backend applies the markup.
            • Otherwise fall back to legacy `colors` + `color_surcharges`
              (no admin editor, just a static name list).
            • Hide entirely when neither is populated. */}
        {product.color_options?.length > 0 ? (
          <SelectRow
            label="Color"
            value={config.color_key || product.color_options[0]?.key || ""}
            onChange={(v) => {
              const sel = product.color_options.find((o) => o.key === v);
              updateConfig({ color_key: v, color: sel?.name || v });
            }}
            options={product.color_options.map((c) => ({
              value: c.key,
              label: c.name + modeSuffix(c),
              image: c.image_url || "",
            }))}
            testid="select-color"
          />
        ) : product.colors?.length > 0 ? (
          <SelectRow
            label="Color"
            value={config.color}
            onChange={(v) => updateConfig({ color: v })}
            // Legacy color list — no surcharge suffix shown (iter17).
            // Customer sees clean color names; Your Price card reflects
            // any applicable markup.
            options={product.colors.map((c) => ({ value: c, label: c }))}
            testid="select-color"
          />
        ) : null}
        {/* Custom Option Groups (iter18) — dynamic per-product dropdowns
            for book / specialty SKUs (Size, Cover Material, Page Count,
            Foil, Deboss, etc.). Each enabled group renders as a select;
            its selection is keyed under `custom_options.<group.key>` so
            the backend can look it up against the product's groups
            without polluting top-level config fields. */}
        {enabledOptionGroups.map((g) => {
          const currentVal = (config.custom_options || {})[g.key] || g.items[0].key;
          const currentItem = g.items.find((it) => it.key === currentVal);
          const linenActive = g.key === "cover_material" && isLinenOption(currentItem?.name);
          const linenChoice = (config.custom_options || {}).linen_choice;
          return (
            <div key={g.key} className="space-y-2">
              <SelectRow
                label={g.label || g.name || g.key}
                value={currentVal}
                onChange={(v) => {
                  const sel = g.items.find((it) => it.key === v);
                  const next = { ...(config.custom_options || {}), [g.key]: v };
                  // Clear linen pick if user switches away from the linen cover variant.
                  if (g.key === "cover_material" && !isLinenOption(sel?.name)) {
                    delete next.linen_choice;
                  }
                  updateConfig({
                    custom_options: next,
                    ...(g.key === "book_size" && sel ? { size: sel.name } : {}),
                  });
                }}
                options={g.items.map((it) => ({ value: it.key, label: it.name }))}
                testid={`select-custom-${g.key}`}
              />
              {linenActive && (
                <LinenRow
                  selectedCode={linenChoice?.code}
                  onClick={() => setLinenPickerOpen(true)}
                />
              )}
            </div>
          );
        })}
        <HangTagFinishing product={product} config={config} updateConfig={updateConfig} />
        {LARGE_FORMAT_CATEGORIES.has(product.service_category) ? (
          /* Large-format: quantity lives inside the per-job Jobs list below
             — hide the global Quantity row in the spec panel. */
          null
        ) : (
          <SelectRow
            label="Quantity"
            value={String(config.quantity)}
            onChange={(v) => updateConfig({ quantity: parseInt(v) })}
            // Quantity options come from one of two sources:
            //   1) `size_anchor_brackets` when the product runs on volume-
            //      tiered size anchors (admin-defined per product, e.g.
            //      [250, 1000, 10000]). Customers buy at the bracket qtys
            //      and the price engine interpolates by qty + area.
            //   2) Legacy `tier_pricing` ladder (15 default rows) when no
            //      anchors are configured.
            // This keeps storefront UX consistent with the admin's chosen
            // pricing template — no mystery qtys, no margin leakage.
            options={(() => {
              const lmBreaks = product.label_model?.quantity_breaks;
              if (lmBreaks?.length) return lmBreaks.map((q) => ({ value: String(q), label: `${Number(q).toLocaleString()}` }));
              const brackets = product.size_anchor_brackets || [];
              const hasAnchors = (product.size_anchors || []).length >= 2;
              if (hasAnchors && brackets.length > 0) {
                return brackets
                  .slice()
                  .sort((a, b) => a - b)
                  .map((q) => ({
                    value: String(q),
                    label: `${Number(q).toLocaleString()}`,
                  }));
              }
              return (product.tier_pricing || []).map((t) => ({
                value: String(t.min_qty),
                label: `${t.min_qty.toLocaleString()}`,
              }));
            })()}
            testid="select-quantity"
          />
        )}
      </div>

      {/* Finishing options (lamination, varnish, foil, pole pockets, hems, grommets) */}
      {(product.laminations?.length || product.varnishes?.length || product.foils?.length
        || product.pole_pockets?.length || product.hems?.length || product.grommets?.length
        || product.finishing_groups?.length) ? (
        <div className="mt-6 pt-5 border-t border-slate-200" data-testid="label-options-section">
          <div className="overline mb-3">Options</div>
          <div className="space-y-4">
            {(() => null)()}
            {product.laminations?.length > 0 && (
              <SelectRow
                label="Lamination"
                value={config.lamination_key}
                onChange={(v) => updateConfig({ lamination_key: v })}
                options={product.laminations.map((l) => ({
                  value: l.key,
                  label: l.name + modeSuffix(l),
                  image: l.image_url || "",
                }))}
                testid="select-lamination"
              />
            )}
            {product.varnishes?.length > 0 && (
              <SelectRow
                label="UV Varnish"
                value={config.varnish_key}
                onChange={(v) => updateConfig({ varnish_key: v })}
                options={product.varnishes.map((v) => {
                  // Clean label only — markups hidden from storefront (iter17).
                  return { value: v.key, label: v.name, image: v.image_url || "" };
                })}
                testid="select-varnish"
              />
            )}
            {product.foils?.length > 0 && (
              <SelectRow
                label="Foil Stamping"
                value={config.foil_key}
                onChange={(v) => updateConfig({ foil_key: v })}
                options={product.foils.map((f) => ({
                  value: f.key,
                  label: f.name + modeSuffix(f),
                  image: f.image_url || "",
                }))}
                testid="select-foil"
              />
            )}
            {product.pole_pockets?.length > 0 && (
              <SelectRow
                label="Pole Pocket"
                value={config.pole_pocket_key}
                onChange={(v) => updateConfig({ pole_pocket_key: v })}
                options={product.pole_pockets.map((p) => ({
                  value: p.key,
                  label: p.name + modeSuffix(p),
                  image: p.image_url || "",
                }))}
                testid="select-pole-pocket"
              />
            )}
            {product.hems?.length > 0 && (
              <SelectRow
                label="Hem"
                value={config.hem_key}
                onChange={(v) => updateConfig({ hem_key: v })}
                options={product.hems.map((p) => ({
                  value: p.key,
                  label: p.name + modeSuffix(p),
                  image: p.image_url || "",
                }))}
                testid="select-hem"
              />
            )}
            {product.grommets?.length > 0 && (
              <SelectRow
                label="Grommet"
                value={config.grommet_key}
                onChange={(v) => updateConfig({ grommet_key: v })}
                options={product.grommets.map((p) => ({
                  value: p.key,
                  label: p.name + modeSuffix(p),
                  image: p.image_url || "",
                }))}
                testid="select-grommet"
              />
            )}
            {(product.finishing_groups || []).map((g) => (
              <SelectRow
                key={g.key}
                label={g.name}
                value={config.finishing_selections?.[g.key] || g.choices?.[0]?.key || ""}
                onChange={(v) => updateConfig({
                  finishing_selections: { ...(config.finishing_selections || {}), [g.key]: v },
                })}
                options={(g.choices || []).map((c) => ({
                  value: c.key,
                  label: c.name + modeSuffix(c),
                  image: c.image_url || "",
                }))}
                testid={`select-fg-${g.key}`}
              />
            ))}
          </div>
        </div>
      ) : null}

      {/* Roll parameters */}
      {product.roll_parameters && (
        <div className="mt-6 pt-5 border-t border-slate-200" data-testid="roll-parameters-section">
          <div className="overline mb-3">Roll parameters</div>
          <div className="space-y-4">
            {product.roll_parameters.core_sizes?.length > 0 && (
              <SelectRow
                label="Core size"
                value={config.core_size}
                onChange={(v) => updateConfig({ core_size: v })}
                options={product.roll_parameters.core_sizes.map((s) => ({ value: s, label: s }))}
                testid="select-core-size"
              />
            )}
            {product.roll_parameters.labels_per_roll?.length > 0 && (
              <SelectRow
                label="Labels per roll"
                value={config.labels_per_roll}
                onChange={(v) => updateConfig({ labels_per_roll: v })}
                options={product.roll_parameters.labels_per_roll.map((n) => ({
                  value: String(n),
                  label: `${n.toLocaleString()}/roll`,
                }))}
                testid="select-labels-per-roll"
              />
            )}
            {product.roll_parameters.outside_diameters?.length > 0 && (
              <SelectRow
                label="Outside diameter"
                value={config.outside_diameter}
                onChange={(v) => updateConfig({ outside_diameter: v })}
                options={product.roll_parameters.outside_diameters.map((d) => ({ value: d, label: d }))}
                testid="select-od"
              />
            )}
            {product.roll_parameters.unwind_directions?.length > 0 && (
              <SelectRow
                label="Unwind direction"
                value={config.unwind_direction}
                onChange={(v) => updateConfig({ unwind_direction: v })}
                options={product.roll_parameters.unwind_directions.map((d) => ({ value: d, label: d }))}
                testid="select-unwind"
              />
            )}
          </div>
        </div>
      )}
      <LinenPicker
        open={linenPickerOpen}
        onClose={() => setLinenPickerOpen(false)}
        selected={(config.custom_options || {}).linen_choice || null}
        onSelect={(l) => {
          updateConfig({
            custom_options: { ...(config.custom_options || {}), linen_choice: l },
          });
        }}
      />
    </>
  );
}

/**
 * LinenRow — orange-button row + selected-linen pill that appears when the
 * customer picks a linen-cloth cover material. Click anywhere on the strip
 * to open the LinenPicker modal. Once selected, swaps to a visual confirmation
 * showing the swatch thumbnail and reference code.
 */
function LinenRow({ selectedCode, onClick }) {
  const choice = selectedCode ? linenCatalog.find((l) => l.code === selectedCode) : null;
  if (!choice) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid="open-linen-picker-btn"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gradient-to-r from-kolt-orange to-[#9A3F12] text-white hover:from-[#9A3F12] hover:to-kolt-orange transition-all rounded-sm shadow-md"
      >
        <span className="flex items-center gap-2.5">
          <Layers className="w-4 h-4" />
          <span className="font-mono text-[11px] tracking-[0.22em] uppercase font-bold">
            Choose your Linen cover
          </span>
        </span>
        <span className="text-[10px] font-mono opacity-90">{linenCatalog.length} options →</span>
      </button>
    );
  }
  return (
    <div
      className="flex items-center gap-3 p-3 border-2 border-kolt-orange bg-orange-50/40 rounded-sm"
      data-testid="linen-selected-tab"
    >
      <img src={choice.image} alt={choice.color}
           className="w-14 h-14 object-cover border border-slate-200 shrink-0 rounded-sm" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-kolt-orange">Linen selected</div>
        <div className="font-bold text-sm text-kolt-inkwell truncate">
          <span className="font-mono">{choice.code}</span> · {choice.color}
        </div>
      </div>
      <button
        type="button"
        onClick={onClick}
        data-testid="change-linen-btn"
        className="text-kolt-orange hover:text-[#9A3F12] flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest font-bold shrink-0"
      >
        <Pencil className="w-3 h-3" /> Change
      </button>
    </div>
  );
}

// Clickable info banner shown under custom W×H inputs when the product
// charges a die-cut tooling fee for non-standard (decimal-inch) sizes.
// Collapsed by default so it doesn't compete with the configurator; click
// to expand the explanation + a one-click "use a standard whole-inch size"
// shortcut for customers who want to avoid the surcharge.
function DieCutHelper({ die_cut_surcharge, widthIn, heightIn, onUseStandard, halfInchSteps = false, shape }) {
  const [open, setOpen] = useState(false);
  const w = Number(widthIn) || 0;
  const h = Number(heightIn) || 0;
  const step = halfInchSteps ? 0.5 : 1;
  const offStep = (v) => Math.abs(v / step - Math.round(v / step)) > 1e-6;
  const customShape = /custom|oval/i.test(shape || "");
  const bigDie = halfInchSteps && Math.min(w, h) >= 7 && Math.max(w, h) >= 9;
  const decimal = offStep(w) || offStep(h) || customShape || bigDie;
  const surchargeLabel = money(Number(die_cut_surcharge) || 0);
  const stepLabel = halfInchSteps ? "½-inch steps (e.g. 2×3, 2.5×3.5, 4×6)" : "whole-inch sizes (e.g. 2×3, 3×4, 4×6)";
  const exampleLabel = halfInchSteps ? "2.75 × 3.68\"" : "2.75 × 3.68\"";
  return (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      className={`mt-2 w-full text-left border ${
        decimal ? "border-kolt-orange bg-kolt-orange/10" : "border-slate-300 bg-slate-50"
      } px-3 py-2.5 transition-colors hover:bg-kolt-orange/10`}
      data-testid="die-cut-helper"
      aria-expanded={open}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={`text-xs font-bold uppercase tracking-wider ${decimal ? "text-kolt-orange" : "text-slate-700"}`}>
          {decimal
            ? `Custom die — +${surchargeLabel} one-time tooling fee applied`
            : halfInchSteps
              ? `Sizes off ½" steps, custom shapes or dies over 7×9" add ${surchargeLabel} (one time)`
              : `Decimal sizes (e.g. 2.75 × 3.68") add ${surchargeLabel} for a custom die`}
        </span>
        <span className="text-[10px] font-mono text-slate-500">{open ? "Hide" : "Why?"}</span>
      </div>
      {open && (
        <div className="mt-2 pt-2 border-t border-current/20 text-[11px] text-slate-700 leading-relaxed">
          <p>
            Roll labels are cut on shared steel dies. Rectangles, squares and circles on {stepLabel} share our existing dies and avoid tooling charges. Any other dimension (e.g. <span className="font-mono font-bold">{exampleLabel}</span>){halfInchSteps ? ", a custom shape, or a very large die" : ""} requires us to manufacture a one-time custom die — that&apos;s the {surchargeLabel} charge, once per order.
          </p>
          {decimal && onUseStandard && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 self-center">Switch to:</span>
              {[[2, 3], [3, 4], [4, 6]].map(([sw, sh]) => (
                <span
                  key={`${sw}x${sh}`}
                  onClick={(e) => { e.stopPropagation(); onUseStandard(sw, sh); }}
                  className="px-2 py-0.5 border border-kolt-slate text-kolt-slate hover:bg-kolt-slate hover:text-white text-[10px] font-mono uppercase tracking-wider transition-colors cursor-pointer"
                  data-testid={`die-cut-standard-${sw}x${sh}`}
                >
                  {sw}×{sh}&quot;
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </button>
  );
}

// SqftSizeInput — feet+inches WxH inputs for products priced per ft²
// (foam boards, banners, posters). Customer enters integers for ft and
// 0-11 (in 0.5 steps) for inches; the component combines to total inches
// and pushes width_in / height_in into the parent config so the price
// engine can do its ⌈area_ft²⌉ × $/ft² math.
//
// Live preview shows raw area, billable (rounded) area, and the
// resulting per-piece price (before material/finishing surcharges).
// Bounds (`min_sqft` / `max_sqft`) are validated here so customers can't
// click "Add to cart" with an out-of-range size.
function SqftSizeInput({ product, config, updateConfig }) {
  // We don't add new keys to `config` — instead we treat width_in /
  // height_in as the source of truth (total inches) and derive ft / in
  // for the UI on every render. This keeps the price-calc payload
  // unchanged (still sends width_in / height_in) and avoids a parallel
  // state to sync.
  const toFtIn = (totalIn) => {
    const n = Math.max(0, Number(totalIn) || 0);
    const ft = Math.floor(n / 12);
    const inch = Math.round((n - ft * 12) * 10) / 10;
    return { ft, inch };
  };
  const setFromFtIn = (axis, ft, inch) => {
    const ftN = Math.max(0, parseInt(ft) || 0);
    const inN = Math.max(0, Math.min(11.5, Number(inch) || 0));
    const total = ftN * 12 + inN;
    updateConfig({ [`${axis}_in`]: total });
  };
  const w = toFtIn(config.width_in);
  const h = toFtIn(config.height_in);
  const wIn = Number(config.width_in) || 0;
  const hIn = Number(config.height_in) || 0;
  const areaSqft = (wIn * hIn) / 144;
  const minSqft = Number(product.min_sqft) || 0;
  const maxSqft = Number(product.max_sqft) || 0;
  // Linear per-piece caps (iter21) — replace the old area cap. Either
  // dimension exceeding its cap → block submit + show red warning.
  const maxWidthIn = Number(product.max_width_in) || 0;
  const maxHeightIn = Number(product.max_height_in) || 0;
  // Linear per-piece minimums (iter18). Either dimension below its floor
  // → block submit + show amber warning (separate from the min_sqft area
  // floor which only adjusts billed area, not blocks the order).
  const minWidthIn = Number(product.min_width_in) || 0;
  const minHeightIn = Number(product.min_height_in) || 0;
  const tooWide = maxWidthIn > 0 && wIn > maxWidthIn;
  const tooTall = maxHeightIn > 0 && hIn > maxHeightIn;
  const tooNarrow = minWidthIn > 0 && wIn > 0 && wIn < minWidthIn;
  const tooShort = minHeightIn > 0 && hIn > 0 && hIn < minHeightIn;
  const tooSmall = minSqft > 0 && areaSqft > 0 && areaSqft < minSqft;
  const tooLarge = (maxSqft > 0 && areaSqft > maxSqft) || tooWide || tooTall;
  const belowMinDim = tooNarrow || tooShort;
  const billable = (areaSqft <= 0)
    ? 0
    : (tooSmall ? Math.ceil(minSqft) : Math.ceil(areaSqft));
  const rate = Number(product.price_per_sqft) || 0;
  const perPiece = billable * rate;
  return (
    <div data-testid="sqft-dimensions" className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
            Width
          </div>
          <div className="flex flex-col gap-1.5">
            <div>
              <input
                type="number" min={0} step={1}
                value={w.ft}
                onChange={(e) => setFromFtIn("width", e.target.value, w.inch)}
                className="input-base h-11 w-full font-mono"
                placeholder="ft"
                data-testid="sqft-width-ft"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">feet</div>
            </div>
            <div>
              <input
                type="number" min={0} max={11.5} step={0.5}
                value={w.inch}
                onChange={(e) => setFromFtIn("width", w.ft, e.target.value)}
                className="input-base h-11 w-full font-mono"
                placeholder="in"
                data-testid="sqft-width-in"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">inches</div>
            </div>
          </div>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
            Height
          </div>
          <div className="flex flex-col gap-1.5">
            <div>
              <input
                type="number" min={0} step={1}
                value={h.ft}
                onChange={(e) => setFromFtIn("height", e.target.value, h.inch)}
                className="input-base h-11 w-full font-mono"
                placeholder="ft"
                data-testid="sqft-height-ft"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">feet</div>
            </div>
            <div>
              <input
                type="number" min={0} max={11.5} step={0.5}
                value={h.inch}
                onChange={(e) => setFromFtIn("height", h.ft, e.target.value)}
                className="input-base h-11 w-full font-mono"
                placeholder="in"
                data-testid="sqft-height-in"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">inches</div>
            </div>
          </div>
        </div>
      </div>
      {tooSmall && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="sqft-min-warning">
          Minimum charge applies: this size ({areaSqft.toFixed(2)} ft²) is below the {minSqft.toFixed(1)} ft² floor —
          you&apos;ll be billed for ⌈{minSqft.toFixed(1)}⌉ = {Math.ceil(minSqft)} ft².
        </div>
      )}
      {tooLarge && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="sqft-max-warning">
          {tooWide && (
            <div>
              Width exceeds the maximum {Math.floor(maxWidthIn / 12)}&apos;{(maxWidthIn % 12).toFixed(1)}&quot; (you entered {wIn.toFixed(1)}&quot;).
            </div>
          )}
          {tooTall && (
            <div>
              Height exceeds the maximum {Math.floor(maxHeightIn / 12)}&apos;{(maxHeightIn % 12).toFixed(1)}&quot; (you entered {hIn.toFixed(1)}&quot;).
            </div>
          )}
          {!tooWide && !tooTall && maxSqft > 0 && (
            <div>
              Size exceeds the maximum {maxSqft.toFixed(1)} ft² per piece (you entered {areaSqft.toFixed(2)} ft²). Please reduce dimensions.
            </div>
          )}
        </div>
      )}
      {belowMinDim && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="sqft-min-dim-warning">
          {tooNarrow && (
            <div>
              Width below the minimum {Math.floor(minWidthIn / 12)}&apos;{(minWidthIn % 12).toFixed(1)}&quot; (you entered {wIn.toFixed(1)}&quot;).
            </div>
          )}
          {tooShort && (
            <div>
              Height below the minimum {Math.floor(minHeightIn / 12)}&apos;{(minHeightIn % 12).toFixed(1)}&quot; (you entered {hIn.toFixed(1)}&quot;).
            </div>
          )}
        </div>
      )}
      {areaSqft > 0 && !tooLarge && !belowMinDim && (
        <div className="text-xs font-mono bg-slate-50 border border-slate-200 px-3 py-2" data-testid="sqft-area-summary">
          <span className="uppercase tracking-[0.18em] text-slate-500">Area</span>
          <span className="ml-2 text-slate-700">{areaSqft.toFixed(2)} ft²</span>
          <span className="mx-2 text-slate-300">→</span>
          <span className="uppercase tracking-[0.18em] text-slate-500">Billable</span>
          <span className="ml-2 text-kolt-orange font-bold">{billable} ft²</span>
          <span className="mx-2 text-slate-300">×</span>
          <span className="text-slate-600">${rate.toFixed(2)}/ft²</span>
          <span className="mx-2 text-slate-300">=</span>
          <span className="text-slate-900 font-bold">${perPiece.toFixed(2)}/piece</span>
        </div>
      )}
    </div>
  );
}

